import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField
} from '@mui/material';
import Validator from 'validator';

import { CompanyDetailsValues, useUpdateCompanyDetails } from '@api';

/**
 * Edit a company's own details, without going through one of its users.
 *
 * `EditAccountModal` saves the company via its first user, so it cannot run
 * on a company with none — which is how every account created from the
 * superadmin starts, and how a Street account stays until its first sync.
 * This writes `PATCH /company/{id}` directly instead.
 */

interface EditCompanyModalProps {
  companyId: string;
  open: boolean;
  initial?: CompanyDetailsValues;
  onClose: () => void;
}

const empty: CompanyDetailsValues = { name: '', website: '', address: '' };

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({
  companyId,
  open,
  initial,
  onClose
}: EditCompanyModalProps) => {
  const [values, setValues] = useState<CompanyDetailsValues>(empty);
  const update = useUpdateCompanyDetails(companyId);

  useEffect(() => {
    if (open) {
      setValues(initial ?? empty);
      update.reset();
    }
    // Reset only when the dialog opens, not when `update` changes identity.
  }, [open, initial]);

  const set = (key: keyof CompanyDetailsValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((prev) => ({ ...prev, [key]: e.target.value }));

  // Same rule as the server's @IsUrl on Companies.website.
  const websiteInvalid = values.website.trim().length > 0 && !Validator.isURL(values.website.trim());

  const submit = () => update.mutate(values, { onSuccess: onClose });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Update company details</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Company name"
            value={values.name}
            onChange={set('name')}
          />
          <TextField
            fullWidth
            size="small"
            label="Website"
            value={values.website}
            onChange={set('website')}
            error={websiteInvalid}
            helperText={websiteInvalid ? 'That does not look like a web address.' : 'Optional.'}
          />
          <TextField
            fullWidth
            size="small"
            label="Address"
            value={values.address}
            onChange={set('address')}
            multiline
            minRows={2}
            helperText="Optional."
          />

          {update.isError && (
            <Alert severity="error">
              {(update.error as Error)?.message ?? 'Could not save the company.'}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!values.name.trim() || websiteInvalid || update.isPending}
          onClick={submit}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCompanyModal;
